import { useTemplateFolderSelection } from "./use-template-folder-selection";
import type { TemplateSelection } from "./ad-studio-template-selection";
import type { BrandScope } from "src/utils/brand-scope";
import { useTemplateFolders } from "src/pages/ad-studio/components/use-template-folders";
import {
  isPublishedAdStudioTemplate,
  filterAdStudioTemplates,
  type AdStudioTemplateCard,
  type AdStudioTemplateDraft,
} from "src/pages/ad-studio/templates/types";
import { useState } from "react";
import { templatesInFolders } from "src/pages/ad-studio/components/template-folder-members";
import {
  folderAndSubfolderIds,
  folderPaths,
  type FileFolder,
} from "src/pages/projects/library/folder-paths";

export function useTemplateFolderPicker({
  templates,
  initialSelection,
  selectedTemplateIds,
  brandScope,
}: {
  templates: (AdStudioTemplateCard | AdStudioTemplateDraft)[];
  initialSelection: TemplateSelection;
  selectedTemplateIds: string[];
  brandScope: BrandScope;
}) {
  const { data, isLoading, isError, refetch } = useTemplateFolders({
    suspense: false,
    useErrorBoundary: false,
  });
  const selection = useTemplateFolderSelection({
    templates,
    data,
    initialSelection,
    selectedTemplateIds,
    brandScope,
  });
  const { availableTemplates, requestedFolderIds, toggleFolder } = selection;
  const [browsedFolderId, setBrowsedFolderId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const folders = data?.ampFileFolders ?? [];
  const options = folderPaths(folders).map(({ folder, path }) => {
    const members = data
      ? templatesInFolders(availableTemplates, [folder.id], data)
      : [];
    const count = members.filter(isPublishedAdStudioTemplate).length;
    return {
      id: folder.id,
      label: path,
      count,
      draftCount: members.filter(
        (template) => !isPublishedAdStudioTemplate(template),
      ).length,
      templates: members,
      selected: requestedFolderIds.includes(folder.id),
    };
  });
  const selectedFolderIds = options
    .filter((folder) => folder.selected)
    .map((folder) => folder.id);
  const browsedFolder = options.find((folder) => folder.id === browsedFolderId);
  const descendants = browsedFolderId
    ? folderAndSubfolderIds(folders, browsedFolderId)
    : new Set(folders.map((folder) => folder.id));
  const folderTiles = folders
    .filter((folder) =>
      search
        ? folder.id !== browsedFolderId &&
          descendants.has(folder.id) &&
          folder.name.toLocaleLowerCase().includes(search.toLocaleLowerCase())
        : folder.parentId === browsedFolderId ||
          (browsedFolderId === null &&
            folder.parentId !== null &&
            !folders.some((parent) => parent.id === folder.parentId)),
    )
    .map((folder) => ({ path: folder.id, name: folder.name }));
  const visibleTemplates = filterAdStudioTemplates(
    browsedFolder?.templates ?? availableTemplates,
    search,
  );
  const trail: Pick<FileFolder, "id" | "name">[] = [];
  const foldersById = new Map(folders.map((folder) => [folder.id, folder]));
  const seen = new Set<string>();
  for (
    let folder: FileFolder | undefined = browsedFolderId
      ? foldersById.get(browsedFolderId)
      : undefined;
    folder && !seen.has(folder.id);
    folder = folder.parentId ? foldersById.get(folder.parentId) : undefined
  ) {
    seen.add(folder.id);
    trail.unshift({ id: folder.id, name: folder.name });
  }
  const browseFolder = (id: string | null) => {
    setBrowsedFolderId(id);
    setSearch("");
  };
  const breadcrumbs = [
    { label: "All templates", onClick: () => browseFolder(null) },
    ...trail.map((folder) => ({
      label: folder.name,
      onClick: () => browseFolder(folder.id),
    })),
  ];
  const includedTemplateIds = new Set(
    options
      .filter((folder) => folder.selected)
      .flatMap((folder) =>
        folder.templates
          .filter(isPublishedAdStudioTemplate)
          .map((template) => template.id),
      ),
  );

  return {
    status: isError
      ? ("error" as const)
      : isLoading
        ? ("loading" as const)
        : ("ready" as const),
    availableTemplates,
    folderBrandProfileId: selection.folderBrandProfileId,
    refetch,
    options,
    selectedFolderIds,
    toggleFolder,
    folderTiles,
    visibleTemplates,
    breadcrumbs,
    browseFolder,
    search,
    setSearch,
    includedTemplateIds,
    browsedFolder,
  };
}
